import React from 'react';
import { useTranslation } from 'react-i18next';
import { Building2, ArrowRight, CheckCircle2 } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import type { DiscoveredOrganization } from '../../redux/slices/organizationSlice';

interface OrganizationCardProps {
    organization: DiscoveredOrganization;
    isCurrentOrganization: boolean;
    onSwitch: (organizationId: string) => void;
    disabled?: boolean;
}

/**
 * OrganizationCard - Presentational Component
 * Displays a single organization with switch button
 * Props: organizationId, isCurrentOrganization, onSwitch callback
 */
const OrganizationCard: React.FC<OrganizationCardProps> = ({
    organization,
    isCurrentOrganization,
    onSwitch,
    disabled = false
}) => {
    const { t } = useTranslation();

    const handleSwitch = () => {
        if (!isCurrentOrganization && !disabled) {
            onSwitch(organization.organization_id);
        }
    };

    // Get role from membership
    const role = organization.membership?.type || 'member';
    const roleLabel = role === 'admin'
        ? t('organization.members.roles.admin')
        : t('organization.members.roles.member');

    return (
        <Card className={`transition-all hover:shadow-md ${isCurrentOrganization ? 'border-gold bg-gold/5' : ''}`}>
            <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                    {/* Organization Info */}
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                            {/* Organization Logo or Icon */}
                            {organization.organization_logo_url ? (
                                <div className="flex-shrink-0 w-10 h-10 rounded-md border border-border/50 overflow-hidden bg-background/50 flex items-center justify-center">
                                    <img
                                        src={organization.organization_logo_url}
                                        alt={organization.organization_name}
                                        className="w-full h-full object-contain"
                                        onError={(e) => {
                                            // Fallback to Building2 icon if logo fails to load
                                            const parent = e.currentTarget.parentElement;
                                            if (parent) {
                                                parent.innerHTML = '<svg class="h-5 w-5 text-muted-foreground" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="16" height="20" x="4" y="2" rx="2" ry="2"/><path d="M9 22v-4h6v4"/><path d="M8 6h.01"/><path d="M16 6h.01"/><path d="M12 6h.01"/><path d="M12 10h.01"/><path d="M12 14h.01"/><path d="M16 10h.01"/><path d="M16 14h.01"/><path d="M8 10h.01"/><path d="M8 14h.01"/></svg>';
                                            }
                                        }}
                                    />
                                </div>
                            ) : (
                                <Building2 className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                            )}
                            <h3 className="font-semibold text-base truncate">
                                {organization.organization_name}
                            </h3>
                            {isCurrentOrganization && (
                                <CheckCircle2 className="h-4 w-4 text-gold flex-shrink-0" />
                            )}
                        </div>

                        {/* Organization Details */}
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Badge variant="outline" className="text-xs">
                                {roleLabel}
                            </Badge>
                        </div>

                        {isCurrentOrganization && (
                            <Badge variant="default" className="mt-2 text-xs">
                                {t('organization.switcher.currentOrganization')}
                            </Badge>
                        )}
                    </div>

                    {/* Switch Button */}
                    {!isCurrentOrganization && (
                        <Button
                            onClick={handleSwitch}
                            disabled={disabled}
                            size="sm"
                            variant="outline"
                            className="flex-shrink-0"
                        >
                            {t('organization.switcher.switchTo')}
                            <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                    )}
                </div>
            </CardContent>
        </Card>
    );
};

export default OrganizationCard;
